import { CLink } from '@/CLink';
import { Dialog, DialogClose, DialogContent, DialogOpen } from '@/DialogPrimitive';
import { PlainBtn, PrimaryBtn } from '@/buttons';
import { ReactComponent as ChevronIcon } from '@a/icons/chevron.svg';
import { ReactComponent as CloseIcon } from '@a/icons/close.svg';
import { ReactComponent as BackIcon } from '@a/icons/left-arrow.svg';
import { ReactComponent as MenuIcon } from '@a/icons/menu.svg';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import useLocationData from '@s/hooks/useLocationData';
import useRouteChange from '@s/hooks/useRouteChange';
import useWindowSize from '@s/hooks/useWindowSize';
import { navigate } from 'gatsby';
import { Fragment, useEffect, useState } from 'react';
import { scrollToAppointmentForm } from './Nav';
import { navBtnStyles, navLinkStyles } from './NavLinkStyles';
import { NavLinksTypes, navLinks } from './NavLinks';
import { theme } from './styles/GlobalStyles';
import { flexSpace, flexStart } from './styles/classes';

const overlayStyles = css`
    display: none;
`;

const contentStyles = css`
    ${flexStart};
    flex-direction: column;
    align-items: center;
    background-image: ${theme.colors.gradient};
    padding: 32px;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    > h3 {
        text-transform: capitalize;
        margin: auto 0 32px;

        + nav {
            margin-top: 0 !important;
        }
    }

    > nav {
        ${flexStart};
        flex-direction: column;
        align-items: center;
        margin: auto;
        width: 100%;
        max-height: calc(100% - 64px);
        overflow-y: auto;
    }

    &[data-state='open'] {
        animation: slideIn 300ms ease-in;
    }

    &[data-state='closed'] {
        animation: slideOut 300ms ease-out;
    }

    @keyframes slideIn {
        from {
            transform: translateX(100vw);
        }
        to {
            transform: translateX(0);
        }
    }

    @keyframes slideOut {
        from {
            transform: translateX(0);
        }
        to {
            transform: translateX(100vw);
        }
    }

    @media (min-height: 720px) {
        padding: 64px;
    }
`;

const Top = styled.div`
    ${flexSpace};
    width: 100%;
    margin-bottom: 32px;

    > button {
        :last-of-type {
            margin-left: auto;
        }
    }
`;

type MobNavProps = {
    text?: string;
    links?: NavLinksTypes;
    allDialogsClosed: boolean;
    closeAllDialogs: () => void;
};

export const MobNav = ({
    text,
    links = navLinks,
    allDialogsClosed,
    closeAllDialogs,
}: MobNavProps) => {
    const [open, setOpen] = useState(false);

    const { width } = useWindowSize();

    useRouteChange(setOpen);

    const { title, category } = useLocationData();

    const appointmentScroll = () => {
        setOpen(false);
        setTimeout(
            () =>
                title === 'contact us'
                    ? navigate('/', {
                          state: {
                              scrollToForm: true,
                          },
                      })
                    : scrollToAppointmentForm(),
            100
        );
    };

    useEffect(() => {
        setOpen(false);
    }, [allDialogsClosed]);

    const currentCategory = category === text || title === text;

    return width < 1024 ? (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogOpen>
                {text ? (
                    <PlainBtn
                        css={navBtnStyles}
                        className={currentCategory ? 'current-nav-category' : ''}
                    >
                        {text} <ChevronIcon />
                    </PlainBtn>
                ) : (
                    <PlainBtn
                        aria-label={open ? 'close navigation menu' : 'open navigation menu'}
                        css={css`
                            @media (min-width: 1024px) {
                                display: none;
                            }
                        `}
                    >
                        {open ? <CloseIcon /> : <MenuIcon />}
                    </PlainBtn>
                )}
            </DialogOpen>

            <DialogContent
                contentStyles={contentStyles}
                overlayStyles={overlayStyles}
                title="mobile nav menu"
            >
                <Top>
                    {text ? (
                        <DialogClose>
                            <PlainBtn aria-label="close sub nav menu">
                                <BackIcon />
                            </PlainBtn>
                        </DialogClose>
                    ) : (
                        ''
                    )}

                    <PlainBtn onClick={closeAllDialogs} aria-label="close nav menu">
                        <CloseIcon />
                    </PlainBtn>
                </Top>

                {text && <h3>{text}</h3>}
                <nav>
                    {links.map((data, i) =>
                        data.links ? (
                            <MobNav
                                text={data.text}
                                links={data.links}
                                key={i}
                                allDialogsClosed={allDialogsClosed}
                                closeAllDialogs={closeAllDialogs}
                            />
                        ) : data.text === 'request appointment' ? (
                            <PrimaryBtn
                                onClick={appointmentScroll}
                                style={{ textTransform: 'capitalize', marginBottom: '1px' }}
                            >
                                {data.text}
                            </PrimaryBtn>
                        ) : (
                            <CLink to={data.link} css={navLinkStyles} key={i}>
                                {data.text}
                            </CLink>
                        )
                    )}
                </nav>
            </DialogContent>
        </Dialog>
    ) : (
        <Fragment />
    );
};
